import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AccountBalanceOutlined,
  ArrowForwardRounded,
  AssignmentTurnedInOutlined,
  DescriptionOutlined,
  GavelOutlined,
  RefreshRounded,
  SearchRounded,
  ShieldOutlined,
  SummarizeOutlined,
  TimelineOutlined,
  VisibilityOutlined,
  WarningAmberRounded,
} from "@mui/icons-material";
import { API_METHOD_GET, API_METHOD_POST } from "../../config/constants";
import { api_request } from "../../store/actions/commonAction";
import {
  FETCH_JOB_ERROR,
  FETCH_JOB_SUCCESS,
  MARUTI_DASHBOARD_ERROR,
  MARUTI_DASHBOARD_LOADING,
  MARUTI_DASHBOARD_SUCCESS,
} from "../../store/types/billTypes";
import CoreSelect from "../../core/inputs/CoreSelect";
import {
  downloadMarutiExcel,
  downloadMarutiPdf,
} from "../../store/actions/billActions";

// Turns "gap_in_title" / "HIGH" into "Gap In Title" / "High".
const toTitleCase = (value) =>
  (value || "")
    .toString()
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

// Returns the file name from a full Windows/Unix path.
const baseName = (filePath) => {
  if (!filePath) return "";
  const parts = filePath.split(/[\\/]/);
  return parts[parts.length - 1];
};

// Flattens a { category: [ {severity, title, description, ...} ] } map
// (as returned for `exceptions` / `litigations`) into a single issue list.
const flattenIssueGroups = (groups) => {
  if (!groups || typeof groups !== "object") return [];
  return Object.entries(groups).flatMap(([category, items]) =>
    category === "litigations"
      ? items?.map((item) => {
          return {
            category: toTitleCase(category),
            severity: toTitleCase("high"),
            title: item.case_number || item.cnr,
            detail:
              (item.case_number || item.cnr) +
              ": " +
              (item.order_date || "") +
              ", " +
              (item.court_name || item.courtName),
            recommendation:
              item.parties_involved?.join(", ") || item.petitioners,
            affectedDoc: item.affected_doc,
          };
        })
      : (Array.isArray(items) ? items : []).map((item) => ({
          category: toTitleCase(category),
          severity: toTitleCase(item.severity),
          title: item.title,
          detail: item.description,
          recommendation: item.recommendation,
          affectedDoc: item.affected_doc,
        })),
  );
};

// Placeholder response for the property review. Remove once
// GET /maruti/job/:jobId/dashboard is wired up on the backend.
const DUMMY_DASHBOARD_DATA = {
  report_title: "DEVELOPER PROPERTY DUE DILIGENCE REPORT",
  batch_id: "394",
  created_at: "",
  property_details: [
    {
      property_index: 1,
      document_names: ["1785415498452.pdf"],
      property_type: "Leasehold Property",
      address:
        "Block 13, Thandapper No. 7365, Re Sy. 14/3, 26/17 and 28/18, Maradu Village, Kanayannoor Taluk, Ernakulam District, Kerala",
      plot_survey_number: "Survey: 14/3",
      latest_owner: "M/s. Koyenco Autos Pvt. Ltd",
      last_verification_date: "2019",
    },
    {
      property_index: 2,
      document_names: [
        "1785415498535.pdf",
        "1785415498587.pdf",
        "1785415498591.pdf",
      ],
      property_type: "Company Owned-Property",
      address:
        "NH 47 Bypass Road, Maradu P.O., Maradu Village, Kanayannur Taluk, Ernakulam District, Kerala",
      plot_survey_number: "Plot: Block No. 13 | Survey: 162/1",
      latest_owner: "Sajan Jose, P.M. Prathap, Koyenco Autos Pvt Ltd",
      last_verification_date: "2004",
    },
  ],
  history_of_buy_sell: [
    {
      property_index: 1,
      property_address:
        "Block 13, Thandapper No. 7365, Re Sy. 14/3, 26/17 and 28/18, Maradu Village, Kanayannoor Taluk, Ernakulam District, Kerala",
      survey_number: "14/3",
      records: [
        {
          year: "2004",
          deed_type: "Document / Registration",
          deed_number: "453/04, 454/04, 455/04",
          buyer: "NA",
          seller: "NA",
          is_chain_break: false,
          chain_status: "INITIAL BASE DEED",
          break_reason: "First recorded deed in title history",
        },
        {
          year: "2015",
          deed_type: "Lease Deed",
          deed_number: "Lease Deed dated 01.10.2015",
          buyer: "M/s Platino Classic Motors India Pvt Ltd",
          seller: "M/s. Koyenco Autos Pvt. Ltd",
          is_chain_break: true,
          chain_status: "CHAIN BREAK",
          break_reason:
            "Previous buyer 'NA' does not match current seller 'M/s. Koyenco Autos Pvt. Ltd' (Match Score: 13%) | Noticeable gap of 11 years between deeds (2004 to 2015)",
        },
        {
          year: "2019",
          deed_type: "Deed of Surrender",
          deed_number: "Doc Nos. 453/04, 454/04, 455/04",
          buyer: "M/s. Koyenco Autos Pvt. Ltd",
          seller: "M/s Platino Classic Motors India Pvt Ltd",
          is_chain_break: false,
          chain_status: "CONTINUOUS",
          break_reason: "",
        },
      ],
    },
  ],
  supporting_documents: [
    {
      doc_type: "Building Permit and Possession Certificate",
      doc_no: "A1(2) 2/05-06",
      verification_date: "27/01/2019",
      file_names: ["1785415498352.pdf"],
      is_duplicate: false,
      duplicate_status: "No Duplicate",
    },
    {
      doc_type: "Encumbrance Certificate",
      doc_no: "1330/19",
      verification_date: "2019-01-30",
      file_names: ["1785415498457.pdf", "1785415498475.pdf"],
      is_duplicate: true,
      duplicate_status: "Duplicate (2 files)",
    },
    {
      doc_type: "Fire NOC",
      doc_no: "F. 8228/09",
      verification_date: "15.10.2009",
      file_names: ["1785415498512.pdf"],
      is_duplicate: false,
      duplicate_status: "No Duplicate",
    },
  ],
  exceptions: {
    gap_in_title: [
      {
        severity: "MEDIUM",
        affected_doc: "1785415498522.pdf",
        title: "Multi-Year Ownership Gap (1974)",
        description: "Noticeable gap of 20 years between deeds (1954 to 1974)",
        recommendation:
          "Verify revenue mutation records or encumbrance certificates for intermediate transactions.",
      },
    ],
    name_mismatch: [
      {
        severity: "HIGH",
        affected_doc: "1785415498518.pdf",
        title: "Chain Break in Copy of Registered Sale Deed (2010)",
        description:
          "Previous buyer 'Classic Marbles' does not match current seller 'NA' (Match Score: 13%)",
        recommendation:
          "Obtain link deed or rectification deed establishing title transfer between parties.",
      },
    ],
  },
  litigations: {},
};

const severityColor = { High: "#c84937", Medium: "#bb7512", Low: "#2e7d67" };

const severityIcon = {
  High: GavelOutlined,
  Medium: DescriptionOutlined,
  Low: WarningAmberRounded,
};

const chainStatusColor = (status) => {
  if (status === "CHAIN BREAK") return "#c0483a";
  if (status === "INITIAL BASE DEED") return "#1d5f9e";
  return "#2d9561"; // CONTINUOUS
};

const OUTPUT_PACKS = [
  {
    key: "pdf",
    label: "Get PDF",
    icon: SummarizeOutlined,
  },
  { key: "excel", label: "Get Excel", icon: TimelineOutlined },
];

function SectionTitle({ icon: Icon, title, action }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ mb: 1 }}
    >
      <Stack direction="row" spacing={1.1} alignItems="center">
        <Box sx={{ color: "#1769aa", display: "grid", placeItems: "center" }}>
          <Icon fontSize="small" />
        </Box>
        <Typography
          sx={{
            color: "#18345b",
            fontSize: 13,
            fontWeight: 800,
            letterSpacing: 0.2,
            textTransform: "uppercase",
          }}
        >
          {title}
        </Typography>
      </Stack>
      {action}
    </Stack>
  );
}

export default function MarutiDashboard() {
  const dispatch = useDispatch();

  const [jobId, setJobId] = useState("");
  const [loadedJobId, setLoadedJobId] = useState("");
  const [inputError, setInputError] = useState("");

  const loading = useSelector((state) => state?.bill?.marutiDashboardLoading);
  const fetchError = useSelector((state) => state?.bill?.marutiDashboardError);
  const dashboardData = useSelector(
    (state) => state?.bill?.marutiDashboardData,
  );
  const uid = useSelector((state) => state?.auth?.uid);
  const docJobs = useSelector((state) => state?.bill?.jobs?.rows);

  useEffect(() => {
    getJobs();
  }, []);

  const getJobs = () => {
    dispatch(
      api_request(
        API_METHOD_GET,
        "/business/all/Jobs",
        {},
        {
          _defaultFilter: encodeURIComponent(
            JSON.stringify({
              _status: "completed",
              createdBy: uid,
            }),
          ),
        },
        null,
        FETCH_JOB_SUCCESS,
        FETCH_JOB_ERROR,
        true,
        false,
      ),
    );
  };

  const fetchDashboard = (id) => {
    // TODO: replace with the real API call once the backend endpoint is ready.
    dispatch(
      api_request(
        API_METHOD_GET,
        `/maruti/job/${id}/dashboard`,
        {},
        {},
        MARUTI_DASHBOARD_LOADING,
        MARUTI_DASHBOARD_SUCCESS,
        MARUTI_DASHBOARD_ERROR,
        true,
        false,
        true,
      ),
    );
  };

  const loadJob = (event) => {
    event.preventDefault();
    console.log("Loading job with ID:", jobId);
    if (!jobId) {
      setInputError("Enter a Job ID to load the property review.");
      return;
    }
    setInputError("");
    setLoadedJobId(jobId);
    fetchDashboard(jobId);
  };

  const handleRefresh = () => {
    if (loadedJobId) fetchDashboard(loadedJobId);
  };

  const handleGenerateOutput = (packKey) => {
    if (!loadedJobId) return;
    if (packKey === "excel") {
      dispatch(downloadMarutiExcel(loadedJobId, "maruti.xlsx"));
    } else if (packKey === "pdf") {
      dispatch(downloadMarutiPdf(loadedJobId, "maruti.pdf"));
    }
  };

  const hasJob = Boolean(loadedJobId) && Boolean(dashboardData) && !loading;

  const properties = dashboardData?.property_details || [];
  const historyGroups = dashboardData?.history_of_buy_sell || [];
  const supportingDocuments = dashboardData?.supporting_documents || [];

  const issues = useMemo(
    () => [...flattenIssueGroups(dashboardData?.exceptions)],
    [dashboardData],
  );

  console.log("issues", issues, dashboardData?.exceptions);

  const [selectedPropertyIndex, setSelectedPropertyIndex] = useState(null);

  useEffect(() => {
    setSelectedPropertyIndex(
      dashboardData?.property_details?.[0]?.property_index ?? null,
    );
  }, [dashboardData]);

  const selectedProperty =
    properties.find((item) => item.property_index === selectedPropertyIndex) ||
    properties[0] ||
    {};

  const selectedHistory = historyGroups.find(
    (item) => item.property_index === selectedProperty.property_index,
  );
  const records = selectedHistory?.records || [];

  const duplicateCount = supportingDocuments.filter(
    (doc) => doc.is_duplicate,
  ).length;
  const openIssueCount = issues.length;

  return (
    <Box
      sx={{
        height: "100vh",
        bgcolor: "#f5f8fc",
        p: { xs: 1.5, md: 2 },
        fontFamily: "Arial, sans-serif",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          maxWidth: 1440,
          mx: "auto",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Grid container spacing={1.5} sx={{ mb: 1.25, flexShrink: 0 }}>
          <Grid item xs={12} sm={7} md={8}>
            <Typography
              component="h1"
              sx={{
                color: "#142b4d",
                fontSize: { xs: 19, md: 21 },
                fontWeight: 800,
                letterSpacing: 0,
              }}
            >
              Property Title Due Diligence
            </Typography>
            <Typography sx={{ color: "#607089", fontSize: 12, mt: 0.25 }}>
              Review title documents, evidence-backed exceptions and compliance
              status for a submitted property job.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={5} md={4}>
            <Paper
              component="form"
              onSubmit={loadJob}
              elevation={0}
              sx={{
                p: 1.1,
                border: "1px solid #dce5f0",
                borderRadius: 2,
                bgcolor: "#fff",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <CoreSelect
                  variant="outlined"
                  noLabel={true}
                  handleChange={(e) => setJobId(e.target.value)}
                  options={docJobs
                    ?.filter((job) => job.type === "maruti-doc-processing")
                    ?.sort(
                      (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
                    )
                    ?.map((job) => {
                      return { value: job.id, label: job.name };
                    })}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={
                    loading ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <SearchRounded fontSize="small" />
                    )
                  }
                  sx={{
                    minWidth: 100,
                    height: 40,
                    bgcolor: "#123d72",
                    textTransform: "none",
                    fontWeight: 700,
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#0c315d", boxShadow: "none" },
                  }}
                >
                  Load
                </Button>
                {hasJob && (
                  <Tooltip title="Refresh">
                    <IconButton
                      onClick={handleRefresh}
                      sx={{
                        border: "1px solid #dce5f0",
                        color: "#345",
                        borderRadius: 1.5,
                        height: 40,
                        width: 40,
                      }}
                    >
                      <RefreshRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        {fetchError && (
          <Alert severity="error" sx={{ mb: 1.25, flexShrink: 0 }}>
            Could not load the property review for Job ID {loadedJobId}. Please
            confirm the Job ID and try again.
          </Alert>
        )}

        {!loadedJobId && !loading && (
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              display: "grid",
              placeItems: "center",
              border: "1px dashed #cbd8e8",
              borderRadius: 2,
              bgcolor: "#fff",
            }}
          >
            <Stack
              alignItems="center"
              spacing={1.2}
              sx={{ color: "#607089", textAlign: "center", px: 3 }}
            >
              <ShieldOutlined sx={{ fontSize: 40, color: "#3f7cb6" }} />
              <Typography sx={{ color: "#213b60", fontWeight: 700 }}>
                Load a property review
              </Typography>
              <Typography sx={{ fontSize: 14 }}>
                Enter the Job ID to view documents, title risks and compliance
                status.
              </Typography>
            </Stack>
          </Paper>
        )}

        {loading && (
          <LinearProgress sx={{ borderRadius: 1, mb: 1.25, flexShrink: 0 }} />
        )}

        {hasJob && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                gap: 1.25,
                flex: 1,
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  flex: { xs: "1 1 auto", lg: "0 0 66%" },
                  maxWidth: { lg: "66%" },
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                  minHeight: 0,
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.25,
                    border: "1px solid #dce5f0",
                    borderRadius: 2,
                    flexShrink: 0,
                  }}
                >
                  <SectionTitle
                    icon={AssignmentTurnedInOutlined}
                    title="Property details"
                    action={
                      <Chip
                        label={`Property ${
                          properties.findIndex(
                            (item) =>
                              item.property_index ===
                              selectedProperty.property_index,
                          ) + 1 || 1
                        } of ${properties.length || 1}`}
                        size="small"
                        sx={{
                          bgcolor: "#e7f0fb",
                          color: "#1d5f9e",
                          fontWeight: 800,
                        }}
                      />
                    }
                  />
                  {properties.length > 1 && (
                    <Stack
                      direction="row"
                      spacing={0.75}
                      sx={{ overflowX: "auto", pb: 1 }}
                    >
                      {properties.map((item, index) => (
                        <Chip
                          key={item.property_index ?? index}
                          label={`#${index + 1} ${
                            item.property_type || "Property"
                          }`}
                          size="small"
                          clickable
                          onClick={() =>
                            setSelectedPropertyIndex(item.property_index)
                          }
                          sx={{
                            fontWeight: 700,
                            fontSize: 11,
                            bgcolor:
                              item.property_index ===
                              selectedProperty.property_index
                                ? "#1d5f9e"
                                : "#eef2f7",
                            color:
                              item.property_index ===
                              selectedProperty.property_index
                                ? "#fff"
                                : "#4b5c74",
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                  <Grid container spacing={1.5}>
                    {[
                      [
                        "Property type",
                        selectedProperty.property_type || "-",
                        "#24703d",
                      ],
                      [
                        "Latest owner",
                        selectedProperty.latest_owner || "-",
                        "#1d5f9e",
                      ],
                      [
                        "Plot / survey number",
                        selectedProperty.plot_survey_number || "-",
                        "#b95623",
                      ],
                      [
                        "Last verification",
                        selectedProperty.last_verification_date || "-",
                        "#7d5cb0",
                      ],
                    ].map(([label, value, color]) => (
                      <Grid item xs={12} sm={6} md={3} key={label}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderLeft: `3px solid ${color}`,
                            bgcolor: "#f8fafc",
                            height: "100%",
                          }}
                        >
                          <Typography
                            sx={{
                              color: "#718198",
                              fontSize: 11,
                              fontWeight: 700,
                              textTransform: "uppercase",
                            }}
                          >
                            {label}
                          </Typography>
                          <Typography
                            sx={{
                              color,
                              fontWeight: 800,
                              mt: 0.5,
                              fontSize: 13,
                            }}
                          >
                            {value}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                    <Grid item xs={12}>
                      <Typography sx={{ color: "#556579", fontSize: 12 }}>
                        {selectedProperty.address ||
                          "Address not available for this property."}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 1.25,
                    border: "1px solid #dce5f0",
                    borderRadius: 2,
                    flexShrink: 0,
                  }}
                >
                  <SectionTitle
                    icon={TimelineOutlined}
                    title="History of buy / sell"
                  />
                  {records.length ? (
                    <Box sx={{ overflowX: "auto", py: 0.25 }}>
                      <Stepper
                        alternativeLabel
                        activeStep={records.length}
                        sx={{ minWidth: 480 }}
                      >
                        {records.map((record, index) => (
                          <Step key={`${record.year}-${index}`} completed>
                            <StepLabel error={record.is_chain_break}>
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 800,
                                  color: "#233a5c",
                                }}
                              >
                                {record.year}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: chainStatusColor(record.chain_status),
                                  textTransform: "uppercase",
                                }}
                              >
                                {record.deed_type}
                              </Typography>
                              <Typography
                                sx={{ fontSize: 11, color: "#697b93" }}
                              >
                                {record.seller && record.seller !== "NA"
                                  ? record.seller
                                  : "—"}{" "}
                                &rarr;{" "}
                                {record.buyer && record.buyer !== "NA"
                                  ? record.buyer
                                  : "—"}
                              </Typography>
                              {record.is_chain_break && (
                                <Tooltip title={record.break_reason || ""}>
                                  <Typography
                                    sx={{
                                      fontSize: 10.5,
                                      fontWeight: 700,
                                      color: "#c0483a",
                                    }}
                                  >
                                    Chain break
                                  </Typography>
                                </Tooltip>
                              )}
                            </StepLabel>
                          </Step>
                        ))}
                      </Stepper>
                    </Box>
                  ) : (
                    <Typography sx={{ color: "#8997a9", fontSize: 13 }}>
                      No ownership history available for this property yet.
                    </Typography>
                  )}
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 1.25,
                    border: "1px solid #dce5f0",
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minHeight: 250,
                    maxHeight: 250,
                    overflow: "hidden",
                  }}
                >
                  <SectionTitle
                    icon={DescriptionOutlined}
                    title="Supporting documents"
                    action={
                      <Button
                        size="small"
                        endIcon={<ArrowForwardRounded />}
                        sx={{ textTransform: "none", fontWeight: 700 }}
                      >
                        View all
                      </Button>
                    }
                  />
                  <Box
                    sx={{
                      overflowX: "auto",
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      minHeight: 0,
                    }}
                  >
                    <Box sx={{ minWidth: 620 }}>
                      <Grid
                        container
                        sx={{
                          px: 1,
                          pb: 0.5,
                          color: "#7a8aa0",
                          fontSize: 11,
                          fontWeight: 800,
                          textTransform: "uppercase",
                        }}
                      >
                        <Grid item xs={4}>
                          Document type
                        </Grid>
                        <Grid item xs={3}>
                          Doc no.
                        </Grid>
                        <Grid item xs={2}>
                          Verified on
                        </Grid>
                        <Grid item xs={1.5}>
                          Files
                        </Grid>
                        <Grid item xs={1.5}>
                          Status
                        </Grid>
                      </Grid>
                    </Box>
                    <Box
                      sx={{
                        minWidth: 620,
                        overflowY: "auto",
                        flex: 1,
                        minHeight: 0,
                      }}
                    >
                      {supportingDocuments.length ? (
                        supportingDocuments.map((document, index) => (
                          <Grid
                            container
                            alignItems="center"
                            key={document.doc_no || index}
                            sx={{
                              px: 1,
                              py: 0.65,
                              borderTop: "1px solid #edf1f6",
                            }}
                          >
                            <Grid item xs={4}>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <DescriptionOutlined
                                  sx={{ color: "#4381ba", fontSize: 16 }}
                                />
                                <Typography
                                  sx={{
                                    color: "#304763",
                                    fontSize: 12.5,
                                    fontWeight: 600,
                                  }}
                                >
                                  {document.doc_type}
                                </Typography>
                              </Stack>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography
                                sx={{ color: "#667990", fontSize: 12 }}
                              >
                                {document.doc_no || "-"}
                              </Typography>
                            </Grid>
                            <Grid item xs={2}>
                              <Typography
                                sx={{ color: "#667990", fontSize: 12 }}
                              >
                                {document.verification_date || "-"}
                              </Typography>
                            </Grid>
                            <Grid item xs={1.5}>
                              <Tooltip
                                title={
                                  (document.file_names || [])
                                    .map(baseName)
                                    .join(", ") || "No files"
                                }
                              >
                                <Stack
                                  direction="row"
                                  spacing={0.4}
                                  alignItems="center"
                                >
                                  <VisibilityOutlined
                                    sx={{ color: "#2a6fae", fontSize: 15 }}
                                  />
                                  <Typography
                                    sx={{ color: "#667990", fontSize: 12 }}
                                  >
                                    {document.file_names?.length || 0}
                                  </Typography>
                                </Stack>
                              </Tooltip>
                            </Grid>
                            <Grid item xs={1.5}>
                              <Chip
                                label={
                                  document.is_duplicate ? "Duplicate" : "Unique"
                                }
                                size="small"
                                sx={{
                                  height: 20,
                                  bgcolor: document.is_duplicate
                                    ? "#fff5e6"
                                    : "#eaf7ef",
                                  color: document.is_duplicate
                                    ? "#ae6d12"
                                    : "#267044",
                                  fontWeight: 700,
                                  fontSize: 10.5,
                                }}
                              />
                            </Grid>
                          </Grid>
                        ))
                      ) : (
                        <Typography
                          sx={{ color: "#8997a9", fontSize: 13, py: 2 }}
                        >
                          No documents found for this job.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Paper>
              </Box>

              <Box
                sx={{
                  flex: { xs: "1 1 auto", lg: "0 0 34%" },
                  maxWidth: { lg: "34%" },
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                  minHeight: 0,
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.25, md: 1.5 },
                    mb: 1.25,
                    flexShrink: 0,
                    border: "1px solid #dce5f0",
                    borderRadius: 2,
                    bgcolor: "#fff",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    spacing={1.5}
                  >
                    <Stack
                      direction="row"
                      spacing={{ xs: 2, sm: 3.5 }}
                      flexWrap="wrap"
                    >
                      {[
                        ["Properties", properties.length],
                        ["Supporting docs", supportingDocuments.length],
                        ["Duplicate flags", duplicateCount],
                        // ["Open exceptions", openIssueCount],
                      ].map(([label, value]) => (
                        <Box key={label}>
                          <Typography
                            sx={{
                              color: "#7a8aa0",
                              fontSize: 10,
                              fontWeight: 700,
                              textTransform: "uppercase",
                            }}
                          >
                            {label}
                          </Typography>
                          <Typography
                            sx={{
                              color: "#283e5d",
                              fontSize: 12.5,
                              fontWeight: 700,
                              mt: 0.2,
                            }}
                          >
                            {value}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Stack>
                </Paper>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.25,
                    border: "1px solid #dce5f0",
                    borderRadius: 2,
                    flexShrink: 0,
                  }}
                >
                  <SectionTitle
                    icon={SummarizeOutlined}
                    title="Output pack generation"
                  />
                  <Grid container spacing={0.75}>
                    {OUTPUT_PACKS.map(({ key, label, icon: PackIcon }) => (
                      <Grid item xs={6} key={key}>
                        <Button
                          onClick={() => handleGenerateOutput(key)}
                          startIcon={<PackIcon sx={{ fontSize: 16 }} />}
                          fullWidth
                          size="small"
                          sx={{
                            justifyContent: "flex-start",
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: 11.5,
                            color: "#233a5c",
                            border: "1px solid #dce5f0",
                            borderRadius: 1.5,
                            px: 1,
                            py: 0.75,
                            "&:hover": { bgcolor: "#f4f8fd" },
                          }}
                        >
                          {label}
                        </Button>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: "1px solid #dce5f0",
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minHeight: 0,
                    maxHeight: 400,
                  }}
                >
                  <SectionTitle
                    icon={WarningAmberRounded}
                    title="Flagged exceptions"
                    action={
                      <Chip
                        label={`${openIssueCount} open`}
                        size="small"
                        sx={{
                          bgcolor: "#fff0eb",
                          color: "#bd4b36",
                          fontWeight: 800,
                        }}
                      />
                    }
                  />
                  <Stack
                    spacing={1}
                    sx={{ overflowY: "auto", pr: 0.5, flex: 1, minHeight: 0 }}
                  >
                    {issues.length ? (
                      issues.map((issue, index) => {
                        const IssueIcon =
                          severityIcon[issue.severity] ||
                          AccountBalanceOutlined;
                        return (
                          <Box
                            key={`${issue.title}-${index}`}
                            sx={{
                              p: 1.35,
                              border: "1px solid #f0e1dc",
                              borderRadius: 1.5,
                              bgcolor: "#fffdfc",
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1.1}
                              alignItems="flex-start"
                            >
                              <IssueIcon
                                sx={{
                                  color:
                                    severityColor[issue.severity] || "#8997a9",
                                  fontSize: 21,
                                  mt: 0.15,
                                }}
                              />
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  spacing={1}
                                >
                                  <Typography
                                    sx={{
                                      color: "#354660",
                                      fontSize: 13,
                                      fontWeight: 800,
                                    }}
                                  >
                                    {issue.title}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      color:
                                        severityColor[issue.severity] ||
                                        "#8997a9",
                                      fontSize: 10,
                                      fontWeight: 800,
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    {issue.severity}
                                  </Typography>
                                </Stack>
                                <Chip
                                  label={issue.category}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: 9.5,
                                    fontWeight: 700,
                                    mt: 0.35,
                                    bgcolor: "#eef2f7",
                                    color: "#4b5c74",
                                  }}
                                />
                                <Typography
                                  sx={{
                                    color: "#6f7d91",
                                    fontSize: 11.5,
                                    lineHeight: 1.45,
                                    mt: 0.45,
                                  }}
                                >
                                  {issue.detail}
                                </Typography>
                                {issue.recommendation && (
                                  <Typography
                                    sx={{
                                      color: "#8997a9",
                                      fontSize: 11,
                                      fontStyle: "italic",
                                      lineHeight: 1.4,
                                      mt: 0.35,
                                    }}
                                  >
                                    {issue.recommendation}
                                  </Typography>
                                )}
                                {issue.affectedDoc && (
                                  <Typography
                                    sx={{
                                      color: "#a3aebd",
                                      fontSize: 10.5,
                                      mt: 0.35,
                                    }}
                                  >
                                    {baseName(issue.affectedDoc)}
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                          </Box>
                        );
                      })
                    ) : (
                      <Typography sx={{ color: "#8997a9", fontSize: 13 }}>
                        No exceptions have been flagged for this job.
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              </Box>
            </Box>
          </Box>
        )}
        {/* <Alert
          severity="info"
          icon={false}
          sx={{
            // mt: 1,
            py: 0.25,
            flexShrink: 0,
            border: "1px solid #d9e6f5",
            color: "#4d6684",
            bgcolor: "#f4f8fd",
            fontSize: 12,
          }}
        >
          Review evidence and resolve every high-severity exception before
          issuing the title report.
        </Alert> */}
      </Box>
    </Box>
  );
}
